import { Map, OrderedSet, List, Record, fromJS } from 'immutable'
import * as actions from './itemsActions'
import * as searchActions from '../../views/Search/searchActions'
import * as popularActions from './popularActions'
import * as userActions from '../../user/userActions'
import * as breakingNewsActions from '../widgets/breakingNewsActions'
import { combineReducers } from 'redux'
import { listQueueLimit, listSize } from '../../lib/items'
import { LOCATION_CHANGE } from 'react-router-redux'


const initialItemsState = Map({
  itemIds: OrderedSet(),
  itemQueueIds: OrderedSet(),
  itemsQueuedTotal: 0,
  batches: Map(),
  loading: false,
  failedToLoad: false,
  loadingNext: false,
  listEnd: false,
  relatedTags: OrderedSet(),
  shouldRefresh: false,
})

const listsInitialState = fromJS({
  'featured': initialItemsState,
  'newest': initialItemsState,
  'personal': initialItemsState,
  'paywalled': initialItemsState,
})

function lists(state = listsInitialState, action) {
  switch (action.type) {
    case actions.FETCH_ITEMS:
    case actions.FETCH_ITEMS_SUCCESS:
    case actions.FETCH_ITEMS_FAILURE:
    case actions.FETCH_NEXT_ITEMS:
    case actions.FETCH_NEXT_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_ITEMS_FAILURE:
    case actions.FETCH_BATCH_ITEMS:
    case actions.FETCH_BATCH_ITEMS_SUCCESS:
    case actions.FETCH_BATCH_ITEMS_FAILURE:
    case actions.QUEUE_ITEM:
    case actions.UNQUEUE_ITEMS:
      return state.set(action.list, itemState(state.get(action.list), action))

    case userActions.SET_ACTIVE_USER_PROFILE:
    case userActions.SET_ACTIVE_PRESET_PROFILE:
      return state.map(list => {
        return list.set('shouldRefresh', true)
      })

    default:
      return state
  }
}

function shouldInitializeListSegments(state = false, action) {
  switch (action.type) {
    case actions.REFRESH_SEGMENT_LIST:
      return action.refresh

    case actions.FETCH_ITEMS:
    case actions.FETCH_CATEGORY_ITEMS:
    case actions.FETCH_TAG_ITEMS:
    case actions.FETCH_MOST_READ_ITEMS:
    case actions.FETCH_MOST_VOTED_ITEMS:
      return true

    default:
      return state
  }
}

const initStateActiveList = Map({
  listType: actions.LIST_TYPE_NONE,
  listName: actions.LIST_NAME_NONE,
})
function activeList(state = initStateActiveList, action) {
  switch (action.type) {
    case actions.ACTIVE_LIST:
      return state
        .set('listType', action.listType)
        .set('listName', action.listName)

    default:
      return state
  }
}

function categories(state = Map(), action) {
  switch (action.type) {
    case actions.FETCH_CATEGORY_ITEMS:
    case actions.FETCH_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_CATEGORY_ITEMS_FAILURE:
    case actions.FETCH_NEXT_CATEGORY_ITEMS:
    case actions.FETCH_NEXT_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_CATEGORY_ITEMS_FAILURE:
    case actions.QUEUE_CATEGORY_ITEM:
    case actions.UNQUEUE_CATEGORY_ITEMS:
    case actions.FETCH_BATCH_CATEGORY_ITEMS:
    case actions.FETCH_BATCH_CATEGORY_ITEMS_FAILURE:
    case actions.FETCH_BATCH_CATEGORY_ITEMS_SUCCESS:
      return state.set(action.category, itemState(state.get(action.category), action))

    default:
      return state
  }
}


function tags(state = Map(), action) {
  switch (action.type) {
    case actions.FETCH_TAG_ITEMS:
    case actions.FETCH_TAG_ITEMS_SUCCESS:
    case actions.FETCH_TAG_ITEMS_FAILURE:
    case actions.FETCH_NEXT_TAG_ITEMS:
    case actions.FETCH_NEXT_TAG_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_TAG_ITEMS_FAILURE:
    case actions.QUEUE_TAG_ITEM:
    case actions.UNQUEUE_TAG_ITEMS:
    case actions.FETCH_BATCH_TAG_ITEMS:
    case actions.FETCH_BATCH_TAG_ITEMS_FAILURE:
    case actions.FETCH_BATCH_TAG_ITEMS_SUCCESS:
      return state.set(action.tag, itemState(state.get(action.tag), action))

    default:
      return state
  }
}

export const ItemRecord = Record({
  id: '',
  title: '',
  link: '',
  sourceId: null,
  timestamp: 0,
  source: '',
  plus_votes: 0,
  minus_votes: 0,
  currentVote: actions.ITEM_VOTE_TYPE_NEUTRAL,
  currentReport: actions.ITEM_REPORT_TYPE_NEUTRAL,
  clicks: 0,
  categoryId: 0,
  tags: OrderedSet(),
  batchId: 0,
  custom: false,
  paywalled: false,
  statsFetchedAt: 0,
  mediaGroup: null,
}, 'item')

function itemFromResponse(item, actionTimestamp) {
  return new ItemRecord({
    id: item.id,
    title: item.title,
    link: item.link,
    sourceId: item.sourceId,
    timestamp: item.timestamp,
    source: item.source,
    plus_votes: item.plus_votes,
    minus_votes: item.minus_votes,
    currentVote: item.currentVote || actions.ITEM_VOTE_TYPE_NEUTRAL,
    currentReport: item.currentReport || actions.ITEM_REPORT_TYPE_NEUTRAL,
    clicks: item.clicks,
    categoryId: item.categoryId,
    tags: OrderedSet(item.tags.map(tag => tag.slug)),
    batchId: item.batch.id,
    custom: item.isCustomItem,
    paywalled: item.isPaywalled,
    mediaGroup: item.mediaGroup ?? null,
    statsFetchedAt: actionTimestamp,
  })
}

function items(state = Map(), action) {
  let item
  switch (action.type) {
    case actions.FETCH_SHARED_ITEM_SUCCESS:
      item = action.response
      return state.set(item.id, itemFromResponse(item, action.timestamp))

    case actions.FETCH_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_ITEMS_SUCCESS:
    case actions.FETCH_BATCH_ITEMS_SUCCESS:
    case popularActions.FETCH_POPULAR_SUCCESS:
    case breakingNewsActions.FETCH_BREAKING_NEWS_SUCCESS:
    case actions.FETCH_CUSTOM_FEED_ITEMS_SUCCESS:
    case actions.FETCH_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_BATCH_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_TAG_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_TAG_ITEMS_SUCCESS:
    case actions.FETCH_BATCH_TAG_ITEMS_SUCCESS:
      return action.response.data.reduce((acc, item) => {
        return acc.set(item.id, itemFromResponse(item, action.timestamp))
      }, state)

    case searchActions.FETCH_SEARCH_SUCCESS:
    case searchActions.FETCH_SEARCH_NEXT_SUCCESS:
      return action.response.data.results.items.reduce((acc, item) => {
        return acc.set(item.id, itemFromResponse(item, action.timestamp))
      }, state)

    case actions.FETCH_MOST_READ_ITEMS_SUCCESS:
    case actions.FETCH_MOST_VOTED_ITEMS_SUCCESS:
      return [].concat.apply([], action.response.data.map(data => data.items)) // flatten
        .reduce((acc, item) => {
          return acc.set(item.id, itemFromResponse(item, action.timestamp))
        }, state)

    case actions.QUEUE_ITEM:
    case actions.QUEUE_CATEGORY_ITEM:
    case actions.QUEUE_TAG_ITEM:
      item = action.item
      return state.set(item.id, itemFromResponse(item, action.timestamp))

    case actions.ITEM_VOTE_SUCCESS:
      return state.update(action.id, item => {
        return item
          .set('plus_votes', action.response.data.data.plus_votes)
          .set('minus_votes', action.response.data.data.minus_votes)
          .set('clicks', action.response.data.data.clicks)
          .set('currentVote', action.voteType)
      })

    case actions.ITEM_REPORT_SUCCESS:
      return state.update(action.id, item => item.set('currentReport', action.reportType))

    case actions.ITEM_CLICK_SUCCESS:
      return state.update(action.id, item => item.set('clicks', item.clicks + 1))

    case actions.FETCH_STATS_FOR_ITEMS_SUCCESS:
      return action.response.data.reduce((acc, item) => {
        return acc
          .setIn([item.id, 'plus_votes'], item.plus_votes)
          .setIn([item.id, 'minus_votes'], item.minus_votes)
          .setIn([item.id, 'clicks'], item.clicks)
          .setIn([item.id, 'statsFetchedAt'], action.timestamp)
      }, state)

    case userActions.FETCH_RECOMMENDED_CATEGORIES_SUCCESS: {
      const items = action.data.map(content => content.items)
        .reduce((acc, item) => {
          return item
        }, Map())

      return items.reduce((acc, item) => {
        return acc.set(item.id, itemFromResponse(item, action.timestamp))
      }, state)
    }
    default:
      return state
  }
}

function itemState(state = initialItemsState, action) {

  switch (action.type) {
    case actions.FETCH_ITEMS:
    case actions.FETCH_CATEGORY_ITEMS:
    case actions.FETCH_TAG_ITEMS:
      return initialItemsState
        .set('loading', true)
        .set('failedToLoad', false)
        .set('shouldRefresh', false)

    case actions.FETCH_ITEMS_FAILURE:
    case actions.FETCH_CATEGORY_ITEMS_FAILURE:
    case actions.FETCH_TAG_ITEMS_FAILURE:
      return state
        .set('loading', false)
        .set('failedToLoad', true)

    case actions.FETCH_ITEMS_SUCCESS:
    case actions.FETCH_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_TAG_ITEMS_SUCCESS: {
      const items = action.response.data
      const itemIds = OrderedSet(items.map(item => item.id))

      return state
        .set('loading', false)
        .set('failedToLoad', false)
        .set('itemIds', itemIds)
        .set('itemQueueIds', OrderedSet([]))
        .set('itemsQueuedTotal', 0)
        .set('relatedTags', OrderedSet(items.reduce((a, item) => {
          return item.tags.reduce((a, tag) => {
            return a.set(tag.slug, a.get(tag.slug) + 1 || 1)
          }, a)
        }, Map()).sort((a, b) => a < b).keys()))
        .set('batches', items.reduce((a, item) => {
          return a.set(item.batch.id, Map({
            itemIds: OrderedSet(),
            size: item.batch.size,
            loading: false,
          }))
        }, state.get('batches')))
    }
    case actions.FETCH_NEXT_ITEMS:
    case actions.FETCH_NEXT_CATEGORY_ITEMS:
    case actions.FETCH_NEXT_TAG_ITEMS:
      return state.set('loadingNext', true)

    case actions.FETCH_NEXT_ITEMS_FAILURE:
    case actions.FETCH_NEXT_CATEGORY_ITEMS_FAILURE:
    case actions.FETCH_NEXT_TAG_ITEMS_FAILURE:
      return state.set('loadingNext', false)

    case actions.FETCH_NEXT_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_NEXT_TAG_ITEMS_SUCCESS: {
      const items = action.response.data
      return state
        .set('loadingNext', false)
        .set('listEnd', items.length < listSize)
        .update('itemIds', itemIds => itemIds.union(OrderedSet(items.map(item => item.id))))
        .set('batches', items.reduce((a, item) => {
          return a.set(item.batch.id, Map({
            itemIds: OrderedSet(),
            size: item.batch.size,
            loading: false,
          }))
        }, state.get('batches')))
    }
    case actions.FETCH_BATCH_ITEMS:
    case actions.FETCH_BATCH_CATEGORY_ITEMS:
      return state.setIn(['batches', action.batch, 'loading'], true)

    case actions.FETCH_BATCH_ITEMS_FAILURE:
    case actions.FETCH_BATCH_CATEGORY_ITEMS_FAILURE:
      return state.setIn(['batches', action.batch, 'loading'], false)

    case actions.FETCH_BATCH_ITEMS_SUCCESS:
    case actions.FETCH_BATCH_CATEGORY_ITEMS_SUCCESS:
    case actions.FETCH_BATCH_TAG_ITEMS_SUCCESS:
      return state
        .setIn(['batches', action.batch, 'itemIds'], OrderedSet(action.response.data.map(item => item.id)))
        .setIn(['batches', action.batch, 'loading'], false)

    case actions.FETCH_BATCH_TAG_ITEMS:
      return state.setIn(['batches', action.batch, 'loading'], true)

    case actions.FETCH_BATCH_TAG_ITEMS_FAILURE:
      return state.setIn(['batches', action.batch, 'loading'], false)

    case actions.UNQUEUE_ITEMS:
    case actions.UNQUEUE_CATEGORY_ITEMS:
    case actions.UNQUEUE_TAG_ITEMS:
      return state
        .update('itemIds', itemIds => state.get('itemQueueIds').union(itemIds))
        .set('itemQueueIds', OrderedSet([]))

    case actions.QUEUE_ITEM:
    case actions.QUEUE_CATEGORY_ITEM:
    case actions.QUEUE_TAG_ITEM: {
      const itemsQueuedTotal = state.get('itemsQueuedTotal', 0)

      if (itemsQueuedTotal >= listQueueLimit) {
        return state
      }

      return state
        .set('itemsQueuedTotal', itemsQueuedTotal + 1)
        .set('itemQueueIds', state.get('itemQueueIds')
          .reverse() // eslint-disable-line unicorn/no-array-reverse
          .union(OrderedSet([action.item.id]))
          .reverse() // eslint-disable-line unicorn/no-array-reverse
        )
        .set('batches', state.get('batches').set(action.item.batch.id, Map({
          itemIds: OrderedSet(),
          size: action.item.batch.size,
          loading: false,
        })))
    }

    default:
      return state
  }
}

const groupedState = Map({
  groups: List(),
  loading: false,
  shouldRefresh: false,
  timestamp: null,
})

const groupItems = responseData => {
  return List(responseData.map(group => {
    const { items, title } = group

    return {
      title,
      itemIds: OrderedSet(items.map(item => item.id)),
    }
  }))
}

function mostRead(state = groupedState, action) {
  switch (action.type) {
    case actions.FETCH_MOST_READ_ITEMS:
      return state
        .set('loading', true)
        .set('groups', List())
        .set('shouldRefresh', false)
        .set('timestamp', null)

    case actions.FETCH_MOST_READ_ITEMS_FAILURE:
      return state
        .set('loading', false)
        .set('timestamp', null)

    case actions.FETCH_MOST_READ_ITEMS_SUCCESS:
      return state
        .set('loading', false)
        .set('groups', groupItems(action.response.data))
        .set('timestamp', action.timestamp)

    case userActions.SET_ACTIVE_USER_PROFILE:
    case userActions.SET_ACTIVE_PRESET_PROFILE:
      return state.set('shouldRefresh', true)

    case LOCATION_CHANGE: // Hack to refresh when repetedly navigating to the same route
      return state.set('shouldRefresh', action.payload.action !== 'POP')

    default:
      return state
  }
}

function mostVoted(state = groupedState, action) {
  switch (action.type) {
    case actions.FETCH_MOST_VOTED_ITEMS:
      return state
        .set('loading', true)
        .set('groups', List())
        .set('shouldRefresh', false)
        .set('timestamp', null)

    case actions.FETCH_MOST_VOTED_ITEMS_FAILURE:
      return state
        .set('loading', false)
        .set('timestamp', null)

    case actions.FETCH_MOST_VOTED_ITEMS_SUCCESS:
      return state
        .set('loading', false)
        .set('groups', groupItems(action.response.data))
        .set('timestamp', action.timestamp)


    case userActions.SET_ACTIVE_USER_PROFILE:
    case userActions.SET_ACTIVE_PRESET_PROFILE:
      return state.set('shouldRefresh', true)

    case LOCATION_CHANGE: // Hack to refresh when repetedly navigating to the same route
      return state.set('shouldRefresh', action.payload.action !== 'POP')

    default:
      return state
  }
}

const userFeedsInitialState = Map({
  itemIds: OrderedSet(),
  loading: false,
  timestamp: null,
  failedToLoad: false,
  shouldUpdate: false,
})

function userFeeds(state = userFeedsInitialState, action) {
  switch (action.type) {
    case actions.FETCH_CUSTOM_FEED_ITEMS:
      return state
        .set('loading', true)
        .set('failedToLoad', false)
        .set('shouldUpdate', false)

    case actions.FETCH_CUSTOM_FEED_ITEMS_FAILURE:
      return state
        .set('loading', false)
        .set('timestamp', action.timestamp)
        .set('failedToLoad', true)
        .set('shouldUpdate', false)

    case actions.FETCH_CUSTOM_FEED_ITEMS_SUCCESS:
      return state
        .set('loading', false)
        .set('itemIds', OrderedSet(action.response.data.map(item => item.id)))
        .set('timestamp', action.timestamp)
        .set('shouldUpdate', false)

    case userActions.SET_ACTIVE_USER_PROFILE:
      return state.set('shouldUpdate', true)

    default:
      return state
  }
}

export default combineReducers({
  lists,
  activeList,
  shouldInitializeListSegments,
  items,
  mostRead,
  mostVoted,
  tags,
  categories,
  userFeeds,
})
